// ADR-0074 §2: the probe vocabulary as code (V105-7-1 contract; V105-7-3 conformance types;
// V105-7-4 rehearsal types).
//
// A probe is data that versioned runner code interprets; a bundle can never add a probe type
// or carry executable code. The SERVER decides pass/fail: each type's `evaluate` applies the
// pass criteria from the stored (signed) manifest to the runner's measurements. A runner's own
// status is advisory and only displayed. Measurements are metadata only and fit the 4 KB
// per-probe cap (counts, hashes, codes, region and domain names), so list-shaped params are
// capped per probe and the generators split long lists across probes.
//
//   type              kinds                     runs as (customer side)
//   health            spike, conformance, rehearsal  read-only HTTP GET of a customer-named URL
//   hook_e2e          spike, conformance        the customer's own `e2e` command (local config)
//   ksi_check         spike, conformance        a registered posture check, read-only credentials
//   residency         spike, conformance        provider read API: the region of each resource
//   egress_allowlist  spike, conformance        a customer-supplied outbound log export (observed)
//   table_parity      rehearsal                 read-only sessions on source + target databases
//   hook_timed        rehearsal                 the customer's own rollback / migrate command, timed
//
// Shared by the server (validate + evaluate) and the customer-side runner (the param shapes), so
// it imports nothing with a runtime beyond host checks and the posture-check names.
import { assertCollectorUrl } from '../connectors/hosts.js';
import { POSTURE_CHECKS } from '../data/posture-checks.js';
const isInt = (v, min, max) => typeof v === 'number' && Number.isInteger(v) && v >= min && v <= max;
const isNum = (v, min, max) => typeof v === 'number' && Number.isFinite(v) && v >= min && v <= max;
const isObj = (v) => !!v && typeof v === 'object' && !Array.isArray(v);
const onlyKeys = (o, keys) => Object.keys(o).every((k) => keys.includes(k));
const HEX64 = /^[a-f0-9]{64}$/;
/** Per-probe list caps so the measurements fit 4 KB (generators split longer lists). */
export const PROBE_LIST_LIMITS = Object.freeze({ residencyResources: 40, parityTables: 20, egressAllowlist: 1000, egressOutside: 50 });
const LOOPBACK = /^(localhost|127\.\d+\.\d+\.\d+|::1|0:0:0:0:0:0:0:1)$/i;
/** A customer-named URL a runner will read: http(s), no credentials, not link-local / metadata / loopback. */
export function probeTargetProblem(raw) {
    try {
        const u = assertCollectorUrl(raw);
        if (LOOPBACK.test(u.hostname.replace(/^\[|\]$/g, '')))
            return 'loopback addresses are never probed';
        return null;
    }
    catch (e) {
        return e instanceof Error ? e.message : 'not an allowed URL';
    }
}
// ── health ────────────────────────────────────────────────────────────────────────
/** `health`: HTTP status and latency of a customer-named URL (read-only, all kinds). */
const health = {
    versions: [1],
    kinds: ['spike', 'conformance', 'rehearsal'],
    validate(params, pass) {
        if (!onlyKeys(params, ['url']) || typeof params.url !== 'string' || params.url.length > 2048)
            return 'health params are { url }';
        const target = probeTargetProblem(params.url);
        if (target)
            return `health url: ${target}`;
        if (!onlyKeys(pass, ['expected_status', 'max_ms']))
            return 'health pass criteria are { expected_status, max_ms }';
        if (!isInt(pass.expected_status, 100, 599))
            return 'health expected_status must be an HTTP status';
        if (!isInt(pass.max_ms, 1, 600_000))
            return 'health max_ms must be 1..600000';
        return null;
    },
    evaluate(_params, pass, m) {
        if (!isInt(m.status, 100, 599) || !isNum(m.latency_ms, 0, 86_400_000))
            return 'error';
        return m.status === pass.expected_status && m.latency_ms <= pass.max_ms ? 'pass' : 'fail';
    },
};
// ── hook_e2e (V105-7-3) ───────────────────────────────────────────────────────────
/** `hook_e2e`: the customer's own end-to-end command (slot `e2e`): exit 0, and a report hash when required. */
const hookE2e = {
    versions: [1],
    kinds: ['spike', 'conformance'],
    validate(params, pass) {
        if (Object.keys(params).length)
            return 'hook_e2e takes no params (the command comes from the customer\'s local configuration)';
        if (!onlyKeys(pass, ['exit_code', 'report_required']) || pass.exit_code !== 0 || typeof pass.report_required !== 'boolean') {
            return 'hook_e2e pass criteria are { exit_code: 0, report_required }';
        }
        return null;
    },
    evaluate(_params, pass, m) {
        if (!isInt(m.exit_code, -1, 255) || !isInt(m.duration_ms, 0, 86_400_000))
            return 'error';
        if (m.report_sha256 !== undefined && (typeof m.report_sha256 !== 'string' || !HEX64.test(m.report_sha256)))
            return 'error';
        if (m.exit_code !== 0)
            return 'fail';
        if (pass.report_required === true && typeof m.report_sha256 !== 'string')
            return 'fail';
        return 'pass';
    },
    explain(_params, pass, m) {
        const out = [];
        if (isInt(m.exit_code, -1, 255) && m.exit_code !== 0)
            out.push(`exit code ${m.exit_code}`);
        if (pass.report_required === true && typeof m.report_sha256 !== 'string')
            out.push('no report');
        return out;
    },
    hookSlot: () => 'e2e',
};
// ── ksi_check (V105-7-3) ──────────────────────────────────────────────────────────
const CHECK_KEYS = new Set(POSTURE_CHECKS.map((c) => c.check_key));
const KIND = /^[a-z][a-z0-9_]{1,40}$/;
const RESOURCE_REF = /^[A-Za-z0-9][A-Za-z0-9:/._@+=-]{0,255}$/;
/**
 * `ksi_check`: a registered posture check run customer-side against customer-named resources with
 * read-only credentials (`credential_ref`). The check's own evaluator runs where the data is (the
 * runner); the server accepts only its boolean verdict for a check key it knows, and an error is
 * never a pass. Each probe carries the reviewed `test_key` it evidences (required by the generator).
 */
const ksiCheck = {
    versions: [1],
    kinds: ['spike', 'conformance'],
    validate(params, pass) {
        if (!onlyKeys(params, ['check_key', 'connector_kind', 'resources']))
            return 'ksi_check params are { check_key, connector_kind, resources? }';
        if (typeof params.check_key !== 'string' || !CHECK_KEYS.has(params.check_key))
            return 'ksi_check check_key must be a registered posture check';
        if (typeof params.connector_kind !== 'string' || !KIND.test(params.connector_kind))
            return 'ksi_check connector_kind must be a connector kind';
        if (params.resources !== undefined && (!Array.isArray(params.resources) || params.resources.length > 100 || params.resources.some((r) => typeof r !== 'string' || !RESOURCE_REF.test(r)))) {
            return 'ksi_check resources are at most 100 resource identifiers';
        }
        if (Object.keys(pass).length)
            return 'ksi_check takes no pass criteria (the check\'s evaluator decides)';
        return null;
    },
    evaluate(_params, _pass, m) {
        if (typeof m.error === 'string' && m.error)
            return 'error';
        if (typeof m.passed !== 'boolean')
            return 'error';
        return m.passed ? 'pass' : 'fail';
    },
};
// ── residency (V105-7-3) ──────────────────────────────────────────────────────────
const REGION = /^[a-z0-9][a-z0-9-]{1,39}$/;
const PROVIDER = /^[a-z0-9][a-z0-9-]{0,40}$/;
/** `residency`: every listed resource's region (provider read API) ∈ allowed_regions; unresolvable fails. */
const residency = {
    versions: [1],
    kinds: ['spike', 'conformance'],
    validate(params, pass) {
        if (!onlyKeys(params, ['provider', 'resources']) || typeof params.provider !== 'string' || !PROVIDER.test(params.provider))
            return 'residency params are { provider, resources }';
        const r = params.resources;
        if (!Array.isArray(r) || r.length === 0 || r.length > PROBE_LIST_LIMITS.residencyResources || r.some((x) => typeof x !== 'string' || !RESOURCE_REF.test(x)) || new Set(r).size !== r.length) {
            return `residency resources are 1..${PROBE_LIST_LIMITS.residencyResources} distinct resource identifiers`;
        }
        const a = pass.allowed_regions;
        if (!onlyKeys(pass, ['allowed_regions']) || !Array.isArray(a) || a.length === 0 || a.length > 50 || a.some((x) => typeof x !== 'string' || !REGION.test(x))) {
            return 'residency pass criteria are { allowed_regions: region codes }';
        }
        return null;
    },
    evaluate(params, pass, m) {
        if (!isObj(m.regions))
            return 'error';
        const allowed = new Set(pass.allowed_regions);
        const regions = m.regions;
        for (const res of params.resources) {
            const region = regions[res];
            if (region !== null && region !== undefined && typeof region !== 'string')
                return 'error';
            if (typeof region !== 'string' || !allowed.has(region))
                return 'fail';
        }
        return 'pass';
    },
    explain(params, pass, m) {
        const allowed = new Set(pass.allowed_regions);
        const regions = isObj(m.regions) ? m.regions : {};
        return params.resources.flatMap((res) => {
            const region = regions[res];
            if (typeof region !== 'string')
                return [`${res}: region not resolved`];
            return allowed.has(region) ? [] : [`${res}: ${region}`];
        });
    },
};
// ── egress_allowlist (V105-7-3) ───────────────────────────────────────────────────
const DOMAIN = /^(?=.{3,253}$)(\*\.)?(?!-)[a-z0-9-]{1,63}(?:\.(?!-)[a-z0-9-]{1,63})+$/;
const EGRESS_SOURCES = ['cloudflare_logpush', 'vpc_flow', 'customer_collector'];
/** True when `domain` is an allowlist entry or a subdomain of a `*.` entry. */
export function domainAllowed(domain, allowlist) {
    const d = domain.trim().toLowerCase().replace(/\.$/, '');
    return allowlist.some((e) => (e.domain.startsWith('*.') ? d.endsWith(e.domain.slice(1)) && d.length > e.domain.length - 1 : d === e.domain));
}
/**
 * `egress_allowlist`, `observed` mode: the runner reads the customer's own outbound log export
 * (the same shapes as the egress upload) over a window, counts distinct domains and reports the
 * ones outside the bundle's allowlist (generated from accepted inventory items, each entry citing
 * its item id). The server re-checks every reported domain against the STORED allowlist and fails
 * on any un-inventoried domain. `enforced` mode (a canary connect) is not offered: ADR-0074 open
 * question 4 (whose canary host) is undecided.
 */
const egressAllowlist = {
    versions: [1],
    kinds: ['spike', 'conformance'],
    validate(params, pass) {
        if (!onlyKeys(params, ['mode', 'source', 'window_hours']) || params.mode !== 'observed')
            return 'egress_allowlist params are { mode: observed, source, window_hours }';
        if (!EGRESS_SOURCES.includes(params.source))
            return `egress_allowlist source must be one of ${EGRESS_SOURCES.join(', ')}`;
        if (!isInt(params.window_hours, 1, 720))
            return 'egress_allowlist window_hours must be 1..720';
        const list = pass.allowlist;
        if (!onlyKeys(pass, ['allowlist']) || !Array.isArray(list) || list.length > PROBE_LIST_LIMITS.egressAllowlist)
            return 'egress_allowlist pass criteria are { allowlist }';
        for (const e of list) {
            if (!isObj(e) || !onlyKeys(e, ['domain', 'item_id']) || typeof e.domain !== 'string' || !DOMAIN.test(e.domain) || typeof e.item_id !== 'string' || !e.item_id || e.item_id.length > 200) {
                return 'each allowlist entry is { domain, item_id } (an accepted inventory item)';
            }
        }
        return null;
    },
    evaluate(_params, pass, m) {
        if (!isInt(m.observed, 0, 10_000_000) || !Array.isArray(m.outside) || m.outside.length > PROBE_LIST_LIMITS.egressOutside)
            return 'error';
        if (m.outside.some((d) => typeof d !== 'string' || d.length > 253))
            return 'error';
        if (m.truncated === true)
            return 'error';
        const list = pass.allowlist;
        return m.outside.some((d) => !domainAllowed(d, list)) ? 'fail' : 'pass';
    },
    explain(_params, pass, m) {
        const list = pass.allowlist;
        return Array.isArray(m.outside) ? m.outside.filter((d) => typeof d === 'string' && !domainAllowed(d, list)).map((d) => `un-inventoried domain ${d}`) : [];
    },
};
// ── table_parity (V105-7-4) ───────────────────────────────────────────────────────
export const SQL_IDENTIFIER = /^[A-Za-z_][A-Za-z0-9_]{0,62}(\.[A-Za-z_][A-Za-z0-9_]{0,62})?$/;
const COLUMN = /^[A-Za-z_][A-Za-z0-9_]{0,62}$/;
/**
 * `table_parity`: per listed table on source and target, a row count and an order-independent hash
 * (sha256 over the sorted per-row canonical hashes, rows keyed by the declared key columns), read
 * in read-only sessions with customer-supplied connection strings. Counts and hashes equal per table.
 */
const tableParity = {
    versions: [1],
    kinds: ['rehearsal'],
    validate(params, pass) {
        const t = params.tables;
        if (!onlyKeys(params, ['tables']) || !Array.isArray(t) || t.length === 0 || t.length > PROBE_LIST_LIMITS.parityTables) {
            return `table_parity params are { tables: 1..${PROBE_LIST_LIMITS.parityTables} }`;
        }
        const names = new Set();
        for (const x of t) {
            if (!isObj(x) || !onlyKeys(x, ['name', 'key_columns']) || typeof x.name !== 'string' || !SQL_IDENTIFIER.test(x.name))
                return 'each table is { name, key_columns } with a plain SQL identifier';
            if (!Array.isArray(x.key_columns) || x.key_columns.length === 0 || x.key_columns.length > 8 || x.key_columns.some((k) => typeof k !== 'string' || !COLUMN.test(k)))
                return `table ${x.name}: key_columns are 1..8 column names`;
            if (names.has(x.name))
                return `duplicate table ${x.name}`;
            names.add(x.name);
        }
        if (Object.keys(pass).length)
            return 'table_parity takes no pass criteria (counts and hashes must be equal)';
        return null;
    },
    evaluate(params, _pass, m) {
        if (!isObj(m.tables))
            return 'error';
        let failed = false;
        for (const t of params.tables) {
            const r = m.tables[t.name];
            if (!isObj(r) || !isObj(r.source) || !isObj(r.target))
                return 'error';
            const s = r.source;
            const g = r.target;
            if (!isInt(s.count, 0, Number.MAX_SAFE_INTEGER) || !isInt(g.count, 0, Number.MAX_SAFE_INTEGER)
                || typeof s.sha256 !== 'string' || !HEX64.test(s.sha256) || typeof g.sha256 !== 'string' || !HEX64.test(g.sha256))
                return 'error';
            if (s.count !== g.count || s.sha256 !== g.sha256)
                failed = true;
        }
        return failed ? 'fail' : 'pass';
    },
    explain(params, _pass, m) {
        const tables = isObj(m.tables) ? m.tables : {};
        return params.tables.flatMap((t) => {
            const r = tables[t.name];
            if (!r?.source || !r?.target)
                return [`${t.name}: not measured`];
            if (r.source.count !== r.target.count)
                return [`${t.name}: ${String(r.source.count)} rows on the source, ${String(r.target.count)} on the target`];
            return r.source.sha256 !== r.target.sha256 ? [`${t.name}: row contents differ`] : [];
        });
    },
};
// ── hook_timed (V105-7-4) ─────────────────────────────────────────────────────────
const PROBE_ID = /^[a-z0-9][a-z0-9_-]{0,63}$/;
/**
 * `hook_timed`: the customer's own `rollback` (or `migrate`) command, timed from its start until the
 * named `health` probe passes again. Pass: exit 0 and duration ≤ max_seconds (the cutover RTO).
 */
const hookTimed = {
    versions: [1],
    kinds: ['rehearsal'],
    validate(params, pass) {
        if (!onlyKeys(params, ['slot', 'health_probe_id']) || (params.slot !== 'rollback' && params.slot !== 'migrate'))
            return 'hook_timed params are { slot: rollback | migrate, health_probe_id }';
        if (typeof params.health_probe_id !== 'string' || !PROBE_ID.test(params.health_probe_id))
            return 'hook_timed health_probe_id names a health probe in the bundle';
        if (!onlyKeys(pass, ['max_seconds']) || !isInt(pass.max_seconds, 1, 86_400))
            return 'hook_timed pass criteria are { max_seconds: 1..86400 }';
        return null;
    },
    evaluate(_params, pass, m) {
        if (!isInt(m.exit_code, -1, 255) || !isInt(m.duration_ms, 0, 7 * 86_400_000))
            return 'error';
        if (typeof m.healthy_again !== 'boolean')
            return 'error';
        return m.exit_code === 0 && m.healthy_again && m.duration_ms <= pass.max_seconds * 1000 ? 'pass' : 'fail';
    },
    explain(_params, pass, m) {
        const out = [];
        if (isInt(m.exit_code, -1, 255) && m.exit_code !== 0)
            out.push(`exit code ${m.exit_code}`);
        if (m.healthy_again === false)
            out.push('health did not return');
        if (isInt(m.duration_ms, 0, 7 * 86_400_000) && m.duration_ms > pass.max_seconds * 1000)
            out.push(`took ${Math.ceil(m.duration_ms / 1000)} s, limit ${String(pass.max_seconds)} s`);
        return out;
    },
    hookSlot: (params) => params.slot,
    refersTo: (params) => [{ probe_id: String(params.health_probe_id), type: 'health' }],
};
const REGISTRY = Object.freeze({
    health, hook_e2e: hookE2e, ksi_check: ksiCheck, residency, egress_allowlist: egressAllowlist, table_parity: tableParity, hook_timed: hookTimed,
});
export function getProbeType(type) {
    return Object.prototype.hasOwnProperty.call(REGISTRY, type) ? REGISTRY[type] : null;
}
export function registeredProbeTypes() {
    return Object.keys(REGISTRY).sort();
}
//# sourceMappingURL=verification-probes.js.map